import { FC, useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Layers, Server, Users, LogOut, Home, Database, Archive, ChevronDown, ChevronRight, Box, Inbox } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import { FaDocker, FaDatabase } from 'react-icons/fa';

export const Layout: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    vms: location.pathname.startsWith('/vms') || location.pathname.startsWith('/vms-backup'),
    volumes: location.pathname.startsWith('/volumes') || location.pathname.startsWith('/volumes-backup')
  });

  const menuItems = [
    { 
      path: '/', 
      label: 'Dashboard', 
      icon: Home 
    },
    { 
      path: '/ambientes', 
      label: 'Ambientes', 
      icon: Layers 
    },
    { 
      id: 'vms',
      label: 'VMs',
      icon: Server,
      subItems: [
        { path: '/vms', label: 'VMs', icon: Server },
        { path: '/vms-backup', label: 'VMs Backup', icon: Archive }
      ]
    },
    { 
      id: 'volumes',
      label: 'Volumes',
      icon: Database,
      subItems: [
        { path: '/volumes', label: 'Volumes', icon: Database },
        { path: '/volumes-backup', label: 'Volumes Backup', icon: Archive }
      ]
    },
    { 
      path: '/container-registry', 
      label: 'Container Registry', 
      icon: FaDocker 
    },
    { 
      path: '/buckets', 
      label: 'Buckets', 
      icon: FaDatabase 
    },
    { 
      path: '/usuarios', 
      label: 'Usuários', 
      icon: Users 
    },
  ];

  const handleToggleMenu = (menuId: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleLogout = () => {
    signOut();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <aside className="w-64 bg-blue-600 text-white shadow-lg flex flex-col shrink-0">
        <div className="p-6 border-b border-blue-700">
          <div className="flex items-center mb-1">
            <img
              src="/logo_m.png"
              alt="MGC Logo"
              className="h-8 w-auto mr-2"
            />
            <h1 className="text-xl font-bold">MGC Control</h1>
          </div>
          {user && (
            <p className="text-sm text-blue-100 mt-1 truncate" title={user.name || user.login}>
              {user.name || user.login}
            </p>
          )}
        </div>
        <nav className="mt-4 flex-1 overflow-y-auto">
          {menuItems.map((item) => {
            // Menu com submenu
            if ('subItems' in item && item.subItems) {
              const isExpanded = expandedMenus[item.id || ''];
              const hasActiveSubItem = item.subItems.some(
                subItem => location.pathname === subItem.path || 
                (subItem.path !== '/' && location.pathname.startsWith(subItem.path))
              );
              
              return (
                <div key={item.id || item.label}>
                  <button
                    onClick={() => handleToggleMenu(item.id || '')}
                    className={clsx(
                      'flex items-center justify-between w-full px-6 py-3 text-sm font-medium transition-colors duration-150 ease-in-out',
                      hasActiveSubItem
                        ? 'bg-blue-700 text-white'
                        : 'text-blue-100 hover:bg-blue-700 hover:text-white'
                    )}
                  >
                    <div className="flex items-center">
                      <item.icon className={clsx(
                        'w-5 h-5 mr-3 shrink-0',
                        hasActiveSubItem ? 'text-white' : 'text-blue-200'
                      )} />
                      <span>{item.label}</span>
                    </div>
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  
                  {/* Submenu */}
                  {isExpanded && (
                    <div className="pl-5 border-l-4 border-blue-600 bg-blue-800" style={{backgroundColor: '#1d4ed8'}}>
                      {item.subItems.map(subItem => {
                        const isActive = location.pathname === subItem.path || 
                          (subItem.path !== '/' && location.pathname.startsWith(subItem.path));
                        
                        return (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={clsx(
                              'flex items-center px-4 py-2 text-sm font-medium transition-colors duration-150 ease-in-out',
                              isActive
                                ? 'bg-blue-900 text-white border-l-4 border-blue-300'
                                : 'text-blue-100 hover:bg-blue-700 hover:text-white'
                            )}
                            style={isActive ? {backgroundColor: '#1e3a8a'} : {}}
                          >
                            <subItem.icon className={clsx(
                              'w-5 h-5 mr-3 shrink-0',
                              isActive ? 'text-white' : 'text-blue-200'
                            )} />
                            <span>{subItem.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            // Menu normal (sem submenu)
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={clsx(
                  'flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ease-in-out',
                  isActive
                    ? 'bg-blue-700 text-white border-l-4 border-blue-300'
                    : 'text-blue-100 hover:bg-blue-700 hover:text-white'
                )}
              >
                <Icon className={clsx(
                    'w-5 h-5 mr-3 shrink-0',
                    isActive ? 'text-white' : 'text-blue-200 group-hover:text-white'
                   )} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="mt-auto p-4 border-t border-blue-700">
            <button
            onClick={handleLogout}
            className="flex w-full items-center px-4 py-2 text-sm font-medium text-blue-100 hover:bg-blue-700 hover:text-white rounded-md transition-colors duration-150 ease-in-out"
            >
            <LogOut className="w-5 h-5 mr-3 text-blue-200 group-hover:text-white" />
            <span>Sair</span>
            </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto p-8">
        <Outlet />
      </main>
    </div>
  );
}